import { PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { ConfirmationDialog } from '@/components/ConfirmationDialog'
import {
  massClrRunsSearchSchema,
  useCancelMassClrRun,
  useMassClrPreparerEligibility,
  useMassClrRuns,
  MASS_CLR_RUN_SCOPES,
} from '@/hooks/queries/credit/useMassClr'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { NO_DATA_AVAILABLE_SYMBOL } from '@/lib/utils/constants'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRightIcon, PlusIcon, XCircleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { MassClrRunRow } from '@/hooks/queries/credit/useMassClr'
import { MassRunStatusBadge } from './-components/MassRunStatusBadge'

export const Route = createFileRoute('/_auth/_modules/credit/credit-limit-reviews/mass/')({
  component: RouteComponent,
  validateSearch: (search) => massClrRunsSearchSchema.parse(search),
})

type Scope = (typeof MASS_CLR_RUN_SCOPES)[number]

/** Who the run sits with right now. While in review nobody is pre-selected (Aug 12 decision) —
 *  the status badge already names the pending slot, so this only resolves recorded people. */
function currentHolder(run: MassClrRunRow): string | null {
  if (run.status === 'sent_back_to_preparer') return run.preparerName
  if (run.status === 'approved') return run.approverName
  return null
}

function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { formatDistanceToNowLocalized, formatDateWithTime } = useLocalizedDate()

  const scope: Scope = search.scope ?? 'all'
  const runsQuery = useMassClrRuns(scope)
  const cancelMutation = useCancelMassClrRun()

  const runs = runsQuery.data?.rows ?? []

  // Walking an ineligible user through the whole selection screen to meet a dead button at the end
  // is the same defect one screen earlier, so the entry point is gated too.
  const canPrepare = useMassClrPreparerEligibility().data?.canPrepare

  const onScopeChange = (value: Scope) => {
    navigate({ search: { scope: value === 'all' ? undefined : value } })
  }

  return (
    <PageContainer maxWidth="wide">
      <Spread align="end">
        <VStack gap="sm">
          <PageHeader>{t('massClr.pageHeader')}</PageHeader>
          <Text tone="muted" className="max-w-2xl text-pretty">
            {t('massClr.pageDescription')}
          </Text>
        </VStack>
        <div className="flex shrink-0 items-center gap-2">
          <Button type="button" variant="secondary" asChild>
            <Link to="/credit/credit-limit-reviews/mass/pending-confirmations">
              {t('massClr.pendingConfirmations.button')}
            </Link>
          </Button>
          <Select value={scope} onValueChange={(value) => onScopeChange(value as Scope)}>
            <SelectTrigger className="bg-background w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MASS_CLR_RUN_SCOPES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`massClr.scope.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canPrepare === false ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                {/* Radix swallows pointer events on disabled buttons — the span carries the hover. */}
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button type="button" disabled>
                      <PlusIcon />
                      {t('massClr.newRun')}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{t('clr.errors.massClrNotEligiblePreparer')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button type="button" asChild>
              <Link to="/credit/credit-limit-reviews/mass/new">
                <PlusIcon />
                {t('massClr.newRun')}
              </Link>
            </Button>
          )}
        </div>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <VStack gap="sm">
            <Text tone="muted">
              {t('datatable.returnedResults', { ns: 'common', count: runs.length })}
            </Text>
            <div className="bg-background-level-1 w-full rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('massClr.table.run')}</TableHead>
                    <TableHead>{t('massClr.table.status')}</TableHead>
                    <TableHead className="text-right">{t('massClr.table.accounts')}</TableHead>
                    <TableHead>{t('massClr.table.preparer')}</TableHead>
                    <TableHead>{t('massClr.table.with')}</TableHead>
                    <TableHead>{t('massClr.table.lastSaved')}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((run) => {
                    const holder = currentHolder(run)
                    const isEditable =
                      run.status === 'draft' || run.status === 'sent_back_to_preparer'
                    return (
                      <TableRow key={run.id}>
                        <TableCell>
                          <VStack gap="none">
                            <Text size="xs" tone="muted" className="font-mono">
                              {run.reference}
                            </Text>
                            <Text weight="medium">{run.name}</Text>
                          </VStack>
                        </TableCell>
                        <TableCell>
                          <MassRunStatusBadge status={run.status} pendingSlot={run.pendingSlot} />
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {run.accountCount.toLocaleString()}
                        </TableCell>
                        <TableCell>{run.preparerName}</TableCell>
                        <TableCell>
                          {holder ?? (
                            <Text tone="muted" as="span">
                              {NO_DATA_AVAILABLE_SYMBOL}
                            </Text>
                          )}
                        </TableCell>
                        <TableCell>
                          {run.updatedAt ? (
                            // The relative time is the glance; the hover is the record. An
                            // approved run can no longer change, so its hover names the approval.
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Text tone="muted" as="span" className="cursor-default">
                                  {formatDistanceToNowLocalized(new Date(run.updatedAt), {
                                    addSuffix: true,
                                  })}
                                </Text>
                              </TooltipTrigger>
                              <TooltipContent>
                                {run.status === 'approved' && run.resolvedAt
                                  ? t('massClr.table.approvedOn', {
                                      date: formatDateWithTime(new Date(run.resolvedAt)),
                                    })
                                  : formatDateWithTime(new Date(run.updatedAt))}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Text tone="muted" as="span">
                              {NO_DATA_AVAILABLE_SYMBOL}
                            </Text>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant={run.status === 'draft' ? 'default' : 'secondary'}
                              asChild
                            >
                              <Link
                                to="/credit/credit-limit-reviews/mass/$runId"
                                params={{ runId: String(run.id) }}
                              >
                                {run.status === 'draft'
                                  ? t('massClr.actions.resume')
                                  : run.status === 'sent_back_to_preparer'
                                    ? t('massClr.actions.rework')
                                    : t('massClr.actions.view')}
                                {isEditable && <ArrowRightIcon />}
                              </Link>
                            </Button>
                            {run.status === 'draft' && (
                              <ConfirmationDialog
                                trigger={
                                  <Button
                                    type="button"
                                    variant="ghost-destructive"
                                    size="icon-sm"
                                    title={t('massClr.actions.cancelDraft')}
                                  >
                                    <XCircleIcon />
                                  </Button>
                                }
                                title={t('massClr.cancelDialog.title', { name: run.name })}
                                description={t('massClr.cancelDialog.description', {
                                  count: run.accountCount,
                                })}
                                confirmButtonContent={t('massClr.cancelDialog.confirm')}
                                isDestructive
                                onConfirm={() => cancelMutation.mutate(run.id)}
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </VStack>
        </CardContent>
      </Card>
    </PageContainer>
  )
}
